import React, { useState, useMemo } from 'react';
import { useAllPayments, UnifiedPayment } from '@/hooks/useAllPayments';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DollarSign, CheckCircle2, Clock, AlertCircle, Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDistanceToNow } from 'date-fns';

export const VenturePaymentsOverview: React.FC = () => {
  const { payments, loading, summary, refetch } = useAllPayments();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [payingTaskId, setPayingTaskId] = useState<string | null>(null);

  const handlePayTask = async (payment: UnifiedPayment) => {
    if (payment.type !== 'task' || !payment.task_id) return;
    
    setPayingTaskId(payment.task_id);
    
    try {
      const { error } = await supabase.functions.invoke('pay-task', {
        body: { taskId: payment.task_id }
      });

      if (error) throw error;

      toast.success('Investment payment completed', {
        description: `Paid $${payment.amount.toFixed(2)} for ${payment.title}`
      });
      
      await refetch();
    } catch (error: any) {
      console.error('Payment error:', error);
      toast.error('Payment failed', {
        description: error.message || 'Failed to process payment'
      });
    } finally {
      setPayingTaskId(null);
    }
  };

  const filteredPayments = useMemo(() => {
    return payments.filter(payment => {
      const matchesSearch = payment.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           payment.deal_title.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
      const matchesType = typeFilter === 'all' || payment.type === typeFilter;
      
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [payments, searchQuery, statusFilter, typeFilter]);

  const upcomingPayments = useMemo(() => {
    const today = new Date();
    const in7Days = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    return payments.filter(p => {
      const dueDate = new Date(p.due_date);
      return p.status !== 'paid' && dueDate >= today && dueDate <= in7Days;
    });
  }, [payments]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <CheckCircle2 className="w-4 h-4" />;
      case 'overdue': return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20';
      case 'overdue': return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20';
      default: return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20';
    }
  };

  const getRowColor = (payment: UnifiedPayment) => {
    if (payment.status === 'overdue') return 'border-l-4 border-l-red-500 bg-red-500/5';
    
    const today = new Date();
    const dueDate = new Date(payment.due_date);
    const in7Days = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    if (payment.status !== 'paid' && dueDate >= today && dueDate <= in7Days) {
      return 'border-l-4 border-l-yellow-500 bg-yellow-500/5';
    }
    
    if (payment.status === 'paid') return 'opacity-60';
    
    return '';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-20" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32 mb-2" />
                <Skeleton className="h-4 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Investment Payments</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(summary.totalAmount)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {payments.length} total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              Paid
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(summary.paidAmount)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.paidCount} payments
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-yellow-600" />
              Pending
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{formatCurrency(summary.pendingAmount)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.pendingCount} payments
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600" />
              Overdue
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{formatCurrency(summary.overdueAmount)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.overdueCount} payments
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Payments Alert */}
      {upcomingPayments.length > 0 && (
        <Alert className="border-yellow-500/50 bg-yellow-500/10">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertDescription>
            <div className="font-semibold mb-2">
              {upcomingPayments.length} payment{upcomingPayments.length > 1 ? 's' : ''} due in the next 7 days ({formatCurrency(summary.upcomingIn7Days)})
            </div>
            <div className="space-y-1 text-sm">
              {upcomingPayments.slice(0, 3).map(payment => (
                <div key={payment.id} className="flex items-center justify-between">
                  <span>{payment.title} - {payment.deal_title}</span>
                  <span className="font-medium">{formatCurrency(payment.amount)}</span>
                </div>
              ))}
              {upcomingPayments.length > 3 && (
                <div className="text-xs text-muted-foreground">
                  +{upcomingPayments.length - 3} more
                </div>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>All Investment Payments</CardTitle>
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search by title or investment..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="schedule">Schedules</SelectItem>
                <SelectItem value="task">Tasks</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {filteredPayments.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No payments found</h3>
              <p className="text-muted-foreground">
                {searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Payments will appear here when investments are created'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredPayments.map(payment => (
                <div
                  key={payment.id}
                  className={`p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors ${getRowColor(payment)}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="font-semibold truncate">{payment.title}</h4>
                        <Badge variant="outline" className="text-xs shrink-0">
                          {payment.type === 'schedule' ? 'Schedule' : 'Task'}
                        </Badge>
                        
                        {/* Task status badge for tasks */}
                        {payment.type === 'task' && payment.task_status && (
                          <Badge 
                            variant="outline" 
                            className={`text-xs shrink-0 ${
                              payment.task_status === 'completed' 
                                ? 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20'
                                : payment.task_status === 'in_progress'
                                ? 'bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/20'
                                : 'bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/20'
                            }`}
                          >
                            {payment.task_status === 'in_progress' ? 'In Progress' : payment.task_status}
                          </Badge>
                        )}
                        
                        <Badge className={`${getStatusColor(payment.status)} shrink-0`}>
                          <div className="flex items-center gap-1">
                            {getStatusIcon(payment.status)}
                            <span className="capitalize">{payment.status}</span>
                          </div>
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{payment.deal_title}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Due {formatDistanceToNow(new Date(payment.due_date), { addSuffix: true })}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-3 sm:flex-row-reverse">
                      <div className="text-right shrink-0">
                        <div className="text-lg font-bold">{formatCurrency(payment.amount)}</div>
                        {payment.payment_date && (
                          <p className="text-xs text-muted-foreground">
                            Paid {formatDistanceToNow(new Date(payment.payment_date), { addSuffix: true })}
                          </p>
                        )}
                      </div>
                      
                      {payment.status !== 'paid' && payment.type === 'task' && (
                        payment.task_status === 'completed' ? (
                          <Button
                            size="sm"
                            onClick={() => handlePayTask(payment)}
                            disabled={payingTaskId === payment.task_id}
                            className="shrink-0"
                          >
                            <DollarSign className="w-4 h-4 mr-1" />
                            {payingTaskId === payment.task_id ? 'Processing...' : 'Pay Now'}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled
                            className="shrink-0"
                          >
                            <Clock className="w-4 h-4 mr-1" />
                            Task {payment.task_status === 'in_progress' ? 'In Progress' : 'Pending'}
                          </Button>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
